import { useCallback, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { fetchCourseModule, fetchModuleCurationStatus } from '../redux/courseModuleSlice'

const POLL_INTERVAL_MS = 45000
const POLL_TIMEOUT_MS = 10 * 60 * 1000

const useModuleCurationPolling = () => {
    const dispatch = useDispatch()
    const runIdRef = useRef(0)
    const timerRef = useRef(null)
    const requestRef = useRef(null)

    const stopPolling = useCallback(() => {
        runIdRef.current += 1
        if (timerRef.current) {
            clearTimeout(timerRef.current)
            timerRef.current = null
        }
        requestRef.current?.abort?.()
        requestRef.current = null
    }, [])

    useEffect(() => stopPolling, [stopPolling])

    const startPolling = useCallback(({
        courseId,
        moduleSlug,
        onCompleted,
        onTimeout,
    }) => {
        stopPolling()
        if (!courseId || !moduleSlug) return

        const runId = runIdRef.current
        const deadline = Date.now() + POLL_TIMEOUT_MS

        const finish = (callback, ...args) => {
            if (runId !== runIdRef.current) return
            runIdRef.current += 1
            if (timerRef.current) {
                clearTimeout(timerRef.current)
                timerRef.current = null
            }
            requestRef.current = null
            callback?.(...args)
        }

        const scheduleNextPoll = (poll) => {
            if (runId !== runIdRef.current) return
            if (Date.now() >= deadline) {
                finish(onTimeout)
                return
            }
            timerRef.current = setTimeout(poll, POLL_INTERVAL_MS)
        }

        const poll = async () => {
            if (runId !== runIdRef.current) return
            if (Date.now() >= deadline) {
                finish(onTimeout)
                return
            }
            if (document.hidden) {
                scheduleNextPoll(poll)
                return
            }

            try {
                const statusRequest = dispatch(fetchModuleCurationStatus({
                    course_id: courseId,
                    module_slug: moduleSlug,
                }))
                requestRef.current = statusRequest
                const status = await statusRequest.unwrap()
                if (runId !== runIdRef.current) return
                requestRef.current = null

                if (status?.curation_status === 'completed') {
                    const courseRequest = dispatch(fetchCourseModule({ course_id: courseId }))
                    requestRef.current = courseRequest
                    const course = await courseRequest.unwrap()
                    finish(onCompleted, status, course)
                    return
                }
            } catch {
                if (runId !== runIdRef.current) return
                requestRef.current = null
            }

            scheduleNextPoll(poll)
        }

        poll()
    }, [dispatch, stopPolling])

    return { startPolling, stopPolling }
}

export default useModuleCurationPolling
